import React, { useState } from 'react';
import { GitMerge, Box, Layers, Scan, Building2, Map, PenTool, Wrench, Construction, Calendar, Calculator, ScanLine, ArrowRight, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { otherServices } from '../data/mock';
import LeadCaptureModal from './LeadCaptureModal';

// Trabalhos Realizados
const completedProjects = [
  {
    client: 'Construtora AG Cunha',
    image: 'https://images.unsplash.com/photo-1766936587760-fcc13617ca3b?w=600&q=80',
    category: 'Construção Civil'
  },
  {
    client: 'Construtora Brookfield',
    image: 'https://images.unsplash.com/photo-1761877676992-0c232a7920f2?w=600&q=80',
    category: 'Construção Civil'
  },
  {
    client: 'CCOTI Exército',
    image: 'https://images.unsplash.com/photo-1669877232732-837ba8f84b86?w=600&q=80',
    category: 'Defesa'
  },
  {
    client: 'Shopping Sul',
    image: 'https://images.unsplash.com/photo-1723896808136-e5a7e0dbe48b?w=600&q=80',
    category: 'Comercial'
  },
  {
    client: 'Secretaria de Infraestrutura de Manaus',
    image: 'https://images.unsplash.com/photo-1763328315740-068ac1381da4?w=600&q=80',
    category: 'Governo'
  },
  {
    client: 'iFly',
    image: 'https://images.unsplash.com/photo-1679505112877-bd4771e71e3b?w=600&q=80',
    category: 'Entretenimento'
  },
  {
    client: 'TRF1',
    image: 'https://images.unsplash.com/photo-1634508943475-541d357f8a96?w=600&q=80',
    category: 'Poder Judiciário'
  },
  {
    client: 'Burger King',
    image: 'https://images.unsplash.com/photo-1703424575944-3f8dd76c8ca9?w=600&q=80',
    category: 'Varejo'
  },
  {
    client: 'Museu Nacional',
    image: 'https://images.unsplash.com/photo-1765920774482-00ddfe797342?w=600&q=80',
    category: 'Cultura'
  }
];

const iconMap = {
  GitMerge,
  Box,
  Layers,
  Scan,
  Building2,
  Map,
  PenTool,
  Wrench,
  Construction,
  Calendar,
  Calculator,
  ScanLine
};

// Novos cards de Projetos
const projectServices = [
  {
    title: 'Projetos de Arquitetura',
    description: 'Projetos arquitetônicos completos em BIM, desde concepção até documentação executiva com alto nível de detalhamento.',
    icon: 'PenTool',
    highlight: true
  },
  {
    title: 'Projetos de Engenharia',
    description: 'Projetos de estruturas e instalações (hidráulica, elétrica, HVAC) totalmente integrados e compatibilizados em ambiente BIM.',
    icon: 'Wrench',
    highlight: true
  },
  {
    title: 'Projetos de Infraestrutura',
    description: 'Projetos de infraestrutura urbana, saneamento, pavimentação e obras civis com modelagem BIM e análise de terreno.',
    icon: 'Construction',
    highlight: true
  },
  {
    title: 'As Built',
    description: 'Levantamento e modelagem de edificações existentes com apoio de nuvem de pontos (laser scanning), garantindo precisão milimétrica na documentação.',
    icon: 'ScanLine',
    highlight: true
  },
  {
    title: 'Planejamento 4D',
    description: 'Integração do cronograma ao modelo 3D, permitindo simulação visual do avanço da obra e otimização do planejamento.',
    icon: 'Calendar',
    highlight: true
  },
  {
    title: 'Orçamentação 5D',
    description: 'Extração automática de quantitativos e integração com custos, gerando orçamentos precisos e atualizados em tempo real.',
    icon: 'Calculator',
    highlight: true
  }
];

const ServicesSection = () => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  const handleRequestQuote = (serviceName) => {
    setSelectedService(serviceName);
    setIsLeadModalOpen(true);
  };

  return (
    <>
    <section id="services" className="py-24 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-red-600/20 text-red-400 border-red-500/30 mb-4">Projetos e Serviços BIM</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4">Projetos e Serviços</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Soluções completas em BIM para projetos de todos os portes.
          </p>
        </div>

        {/* Seção de Projetos - Novos Cards */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">Projetos BIM</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectServices.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card
                  key={index}
                  className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-red-500/30 hover:border-red-500 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10 flex flex-col"
                >
                  <CardHeader>
                    <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-600/30 transition-colors">
                      <Icon className="text-red-500" size={32} />
                    </div>
                    <CardTitle className="text-white text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-grow">
                    <CardDescription className="text-gray-400 text-base leading-relaxed flex-grow">{service.description}</CardDescription>
                    <Button 
                      onClick={() => handleRequestQuote(service.title)}
                      className="mt-4 bg-red-600 hover:bg-red-700 text-white w-full group"
                      data-testid={`quote-btn-${index}`}
                    >
                      Solicitar Orçamento
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Seção de Outros Serviços */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">Outros Serviços</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherServices.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card
                  key={index}
                  className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all duration-300 hover:scale-105"
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-red-600/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-red-500" size={28} />
                    </div>
                    <CardTitle className="text-white">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Seção de Trabalhos Realizados */}
        <div>
          <h3 className="text-2xl font-bold text-white mb-3 text-center">Trabalhos Realizados</h3>
          <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
            Conheça alguns dos projetos que desenvolvemos para nossos clientes
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {completedProjects.map((project, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl aspect-square cursor-pointer"
              >
                <img
                  src={project.image}
                  alt={project.client}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <Badge className="bg-red-600/80 text-white text-xs mb-1">
                    {project.category}
                  </Badge>
                  <h4 className="text-white font-semibold text-sm leading-tight">
                    {project.client}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    <LeadCaptureModal 
      isOpen={isLeadModalOpen} 
      onClose={() => setIsLeadModalOpen(false)} 
      preSelectedService={selectedService}
    />
    </>
  );
};

export default ServicesSection;